// ** Modules
import React from 'react';

// ** UI
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

// ** Components
import Section from '@/components/common/Section';
import AccordionWrapper from './AccordionWrapper';
import TextBlockComponent from './TextBlockComponent';
import InnungElementComponent from './InnungElementComponent';
import DownloadItemsComponent from './DownloadItemsComponent';

// ** Types
import type { User } from 'next-auth';
import type { StrapiComponentProps } from '@/types/strapi';
import type { ComponentElementsAccordion, ComponentBlocksAccordionGroup as AccordionGroupProps } from '@/types/graphql';

type ComponentBlocksAccordionGroupProps = StrapiComponentProps<AccordionGroupProps>;

export type AccordionUIProps = {
	index: number;
	isExpanded: boolean;
	isActive: boolean;
	paused: boolean;
	toggle: () => void;
	autoAdvanceDuration: number;
	host: string;
	currentUser?: User | null;
	handleCountdownComplete?: () => void;
};

export type AccordionWrapperProps = AccordionUIProps & {
	id: string;
	title: string;
	children: React.ReactNode;
};

export type AccordionSubComponentProps = ComponentElementsAccordion & AccordionUIProps;

const ComponentBlocksAccordionGroup = ({
	host,
	accordion = [],
	maxWidthPx,
	containerSx,
	accordionGroupTitle,
	// autoAdvanceDuration,
	allowDefaultExpanded = true,
	allowMultipleExpanded = false
}: ComponentBlocksAccordionGroupProps) => {
	const autoAdvanceEnabled = accordion.length >= 2;

	const [activeIndex, setActiveIndex] = React.useState<number | null>(null);
	const [manualExpandedIds, setManualExpandedIds] = React.useState<string[]>([]);
	const [isPaused, setIsPaused] = React.useState(false);
	const initializedRef = React.useRef(false);

	React.useEffect(() => {
		if (initializedRef.current || accordion.length === 0 || !allowDefaultExpanded) return;
		initializedRef.current = true;

		const explicitIdx = accordion.findIndex((item) => item?.expandedAccordionItem);
		const startIdx = explicitIdx !== -1 ? explicitIdx : 0;
		const startItem = accordion[startIdx];

		if (autoAdvanceEnabled) {
			setActiveIndex(startIdx);
		} else if (startItem) {
			setManualExpandedIds([startItem.id]);
		}
	}, [accordion, allowDefaultExpanded, autoAdvanceEnabled]);

	const handleCountdownComplete = React.useCallback(() => {
		if (!autoAdvanceEnabled) return;
		setActiveIndex((current) => (current !== null ? (current + 1) % accordion.length : 0));
	}, [autoAdvanceEnabled, accordion.length]);

	const toggle = React.useCallback(
		(id: string, index: number) => {
			setIsPaused(true);
			setActiveIndex(index);
			setManualExpandedIds((prev) => {
				const isExpanded = prev.includes(id);
				if (allowMultipleExpanded) {
					return isExpanded ? prev.filter((x) => x !== id) : [...prev, id];
				}
				return isExpanded ? [] : [id];
			});
		},
		[allowMultipleExpanded]
	);

	const variantComponents: Record<string, React.ComponentType<AccordionSubComponentProps>> = {
		innung: InnungElementComponent,
		files: DownloadItemsComponent,
		textBlock: TextBlockComponent
	};

	return (
		<Section maxWidthPx={maxWidthPx} containerSx={containerSx}>
			{accordionGroupTitle && (
				<Typography variant="h4-primary" sx={{ mb: 6 }}>
					{accordionGroupTitle}
				</Typography>
			)}

			<Box sx={{ width: '100%' }}>
				{accordion.map((item, index) => {
					if (!item) return null;
					const isAutoActive = autoAdvanceEnabled && activeIndex === index && !isPaused;
					const isManualActive = manualExpandedIds.includes(item.id);
					const isExpanded = isAutoActive || isManualActive;

					const ContentComponent = variantComponents[item.elementType];

					return (
						<AccordionWrapper
							key={item.id}
							id={item.id}
							title={item.elementName}
							index={index}
							paused={isPaused}
							host={host}
							isExpanded={isExpanded}
							isActive={isAutoActive}
							toggle={() => toggle(item.id, index)}
							autoAdvanceDuration={5}
							handleCountdownComplete={handleCountdownComplete}
						>
							{ContentComponent ? (
								<ContentComponent
									host={host}
									index={index}
									isExpanded={isExpanded}
									isActive={isAutoActive}
									paused={isPaused}
									toggle={() => toggle(item.id, index)}
									autoAdvanceDuration={5}
									{...item}
								/>
							) : (
								<Typography>Unknown element type: {item.elementType}</Typography>
							)}
						</AccordionWrapper>
					);
				})}
			</Box>
		</Section>
	);
};

export default ComponentBlocksAccordionGroup;
